import request from "."

import { AccountParams, CommonList, CommonListParams, CommonStateEnum } from "types/common";
import { Token } from "types/token";
import { AreaEnum, User, UserAdd, UserUpdate, UserPermissionEnum, UserSetting, UserUpdatePermission, UserPermission } from "types/user";
import { isArray, isOrg, isString, isStringEmpty, isUser, isEnum } from 'utils/checkTool';
import { cleanToOrg, cleanToUser } from 'utils/cleanData';
import { Org } from 'types/org';


const USER_URL = '/wms/user';
type UpdateBasicType = 'name1' | 'name2' | 'avatar' | 'mobile'

const USER_APIS = {
  login: USER_URL + '/login',
  add: USER_URL + '/add',
  list: (type: CommonList, status: CommonStateEnum) => USER_URL + `${status === CommonStateEnum.ACTIVE ? '' : '/disabled'}/${type}`,
  fetch: (id?: string) => USER_URL + `${id ? '/' + id : ''}/fetch`,
  fetchs: USER_URL + '/fetchs',
  orgs: (id: string) => USER_URL + `/${id}/orgs`,
  disable: (id: string) => USER_URL + `/${id}/disable`,
  enable: (id: string) => USER_URL + `/${id}/enable`,
  update: (id: string) => USER_URL + `/${id}/update`,
  updateFiled: (id: string, type: UpdateBasicType) => USER_URL + `/${id}/${type}/update`,
  updatePassword: (id: string) => USER_URL + `/${id}/pass/set`,
  updatePermission: (id: string) => USER_URL + `/${id}/permission/set`,
  updateSetting: (id: string) => USER_URL + `/${id}/setting/set`,
  updateMobile: (id: string) => USER_URL + `/${id}/mobile/update`,
}

export const login = (areaCode: AreaEnum, mobile: string, password: string) => {
  return request<Token>(USER_APIS.login, {
    params: {
      account: { areaCode, mobile },
      secure: { password }
    }
  })
}

export const userAdd = (user: UserAdd) => {
  if (isStringEmpty(user.mobile) || !isString(user.mobile) || isStringEmpty(user.areaCode) || !isString(user.areaCode)) {
    return Promise.reject(10012)
  }
  return request<User, UserAdd>(USER_APIS.add, { params: { body: user } })
    .then(res => {
      if (isUser(res.body)) {
        return Promise.resolve(cleanToUser(res.body))
      } else {
        return Promise.reject(10013)
      }
    })
    .catch(err => Promise.reject(err))
}

export const userList = (type: CommonList, status: CommonStateEnum, params?: CommonListParams) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012)
  }
  return request<CommonListParams<User>, string>(USER_APIS.list(type, status), { params })
    .then(res => {
      if (isArray<User>(res.body)) {
        return Promise.resolve({
          body: res.body.filter(user => isUser(user))?.map(user => cleanToUser(user)),
          pagination: res.pagination
        })
      } else {
        return Promise.reject(10013)
      }
    })
    .catch(err => Promise.reject(err))
}

export const userFetch = (id?: string) => {
  if (id && !isString(id) && isStringEmpty(id)) {
    return Promise.reject(10012)
  }
  return request<User, string>(USER_APIS.fetch(id))
    .then(res => {
      if (isUser(res.body)) {
        return Promise.resolve(cleanToUser(res.body))
      } else {
        return Promise.reject(10013)
      }
    })
    .catch(err => Promise.reject(err))
}

export const userOrgs = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012)
  }
  return request<Array<Org>, string>(USER_APIS.orgs(id))
    .then(orgs => {
      if (isArray<Org>(orgs)) {
        return Promise.resolve(orgs.filter(org => isOrg(org))?.map(org => cleanToOrg(org)))
      } else {
        return Promise.reject(10013)
      }
    })
    .catch(err => Promise.reject(err))
}

export const userDisable = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012)
  }
  return request<void>(USER_APIS.disable(id))
}

export const userEnable = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012)
  }
  return request<void>(USER_APIS.enable(id))
}

export const userUpdateFiled = <T>(id: string, key: UpdateBasicType, value: T) => {
  if (!isString(id) || isStringEmpty(id) || !isString(value) || isStringEmpty(value)) {
    return Promise.reject(10012)
  }
  if (key === 'avatar' || key === 'name1' || key === 'name2') {
    if (!isString(value) || isStringEmpty(value)) {
      return Promise.reject(10012)
    }
  } else if (key === 'mobile') {
    const account = value as AccountParams;
    if (!isEnum(account.areaCode, AreaEnum) || (!isString(account.mobile) || isStringEmpty(account.mobile))) {
      return Promise.reject(10012)
    }
  }
  return request<void, T>(USER_APIS.updateFiled(id, key), { params: { body: value } })
}

export const userUpdatePassword = (id: string, password: string) => {
  if (!isString(id) || isStringEmpty(id) || !isString(password) || isStringEmpty(password)) {
    return Promise.reject(10012)
  }
  return request<void, string>(USER_APIS.updatePassword(id), { params: { body: password } })
}

export const userUpdate = (id: string, info: UserUpdate) => {
  if (!isString(id) || isStringEmpty(id)) {
    if (!isEnum(info.areaCode, AreaEnum) || !isString(info.mobile) || isStringEmpty(info.mobile)) {
      if (!isString(info.name1) || isStringEmpty(info.name1)
        || !isString(info.name2) || isStringEmpty(info.name2)
        || !isString(info.avatar) || isStringEmpty(info.avatar)) {
        return Promise.reject(10012)
      }
    }
  }

  return request<void, UserUpdate>(USER_APIS.update(id), { params: { body: info } })
}

export const userUpdatePermission = (id: string, info: UserUpdatePermission) => {
  if (!isString(id) || isStringEmpty(id) || !isArray<UserPermissionEnum>(info.permissions) || (info.orgId !== null && !isString(info.orgId) && !isStringEmpty(info.orgId))) {
    return Promise.reject(10012)
  }
  return request<Array<UserPermission>, UserUpdatePermission>(USER_APIS.updatePermission(id), { params: { body: info } })
}

export const userUpdateSetting = (id: string, value: Array<UserSetting>) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012)
  }
  return request<void, Array<UserSetting>>(USER_APIS.updateSetting(id), { params: { body: value } })
}

export const userUpdateMobile = (id: string, info: UserUpdate) => {
  if (!isString(id) || isStringEmpty(id)) {
    if (!isEnum(info.areaCode, AreaEnum) || !isString(info.mobile) || isStringEmpty(info.mobile)) {
      return Promise.reject(10012)
    }
  }
  return request<void, UserUpdate>(USER_APIS.updateMobile(id), { params: { body: info } })
}

export const userFetchs = (params?: CommonListParams) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012)
  }
  return request<CommonListParams<User>, string>(USER_APIS.fetchs, { params })
    .then(res => {
      if (isArray<User>(res.body)) {
        return Promise.resolve({
          body: res.body.filter(user => isUser(user))?.map(user => cleanToUser(user)),
          pagination: res.pagination
        })
      } else {
        return Promise.reject(10013)
      }
    })
    .catch(err => Promise.reject(err))
}
